export type JwtPayload = Record<string, unknown> & {
  cartId?: string;
  exp?: number;
  type?: string;
  id?: string;
  email?: string;
};

export function decodeJwtPayload(token: string | null | undefined): JwtPayload | null {
  const parts = String(token || '').split('.');
  if (parts.length < 2) return null;

  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload);
    const unicodePayload = decodeURIComponent(
      Array.from(decodedPayload)
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`)
        .join('')
    );
    return JSON.parse(unicodePayload);
  } catch {
    try {
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
      return JSON.parse(atob(paddedPayload));
    } catch {
      return null;
    }
  }
}

export function getJwtCartId(tokenOrPayload: string | JwtPayload | null | undefined): string | null {
  const payload = typeof tokenOrPayload === 'string'
    ? decodeJwtPayload(tokenOrPayload)
    : tokenOrPayload;

  if (!payload) return null;

  const cartId = payload.cartId ?? payload['cart_id'] ?? payload['cartID'];
  return typeof cartId === 'string' && cartId.trim() ? cartId.trim() : null;
}

export function hasMatchingJwtCartId(
  leftTokenOrPayload: string | JwtPayload | null | undefined,
  rightTokenOrPayload: string | JwtPayload | null | undefined
): boolean {
  const leftCartId = getJwtCartId(leftTokenOrPayload);
  const rightCartId = getJwtCartId(rightTokenOrPayload);

  return !!leftCartId && !!rightCartId && leftCartId === rightCartId;
}

export function isJwtExpired(
  tokenOrPayload: string | JwtPayload | null | undefined,
  nowMs = Date.now()
): boolean {
  const payload = typeof tokenOrPayload === 'string'
    ? decodeJwtPayload(tokenOrPayload)
    : tokenOrPayload;

  if (!payload) return true;

  const exp = Number(payload.exp);
  if (!Number.isFinite(exp)) return false;

  return exp * 1000 <= nowMs;
}

export function isJwtWithCartIdUsable(token: string | null | undefined): boolean {
  const payload = decodeJwtPayload(token);
  return !!getJwtCartId(payload) && !isJwtExpired(payload);
}
